/**
 * The guided first day, and the systems it keeps back until their turn.
 *
 * D: "The game is confusing and close to unplayable for a new player." So a
 * week that starts at the hiring screen starts held by the hand: one plain
 * instruction on screen at a time, and the next one only once the last is
 * done. Day one teaches exactly this, in this order and nothing before its
 * turn: the locker, the clock, the board and the first room, picking a thing
 * up, setting it on its spot, filing the room; then clocking out and sleeping.
 *
 * Everything else the building can do is kept back (`UNLOCKS`) and comes in
 * over the next few days, each with one short instruction the first time it is
 * any use (`TIPS`). A week that did not start at the hiring screen (every
 * reset the scripts make, and every save from before this) has no tutorial at
 * all, and everything is open, so nothing measured can move.
 *
 * Pure. It is handed a plain reading of the game each frame and answers with
 * the one thing to say and where it is; it changes nothing but its own place
 * in the list. `main.js` builds the reading and does the hiding.
 */

/**
 * The day each system opens, counted from the hiring day (1). Not listed:
 * open from the start.
 */
export const UNLOCKS = Object.freeze({
  talk: 2,
  wheel: 2,
  sheet: 2,
  carts: 2,
  pickups: 2,
  snap: 2,
  timescale: 2,
  objective: 2,
  throw: 3,
  classes: 3,
  beats: 3,
  standings: 3,
  consumables: 4,
  pranks: 4,
  strikes: 4,
  jobs: 4,
});

/**
 * How fast the clock runs on the guided day until the first room is filed: a
 * quarter. A game minute is a real second, and the day starts at ten past one
 * for a two o'clock shift, so at full speed a new starter who stopped to read
 * had fifty seconds before they were late and written up for it.
 */
export const GUIDED_PACE = 0.25;

/**
 * And on the next two days, half. A new starter on day two walks a room on
 * foot, two rounds and twelve chairs a trip, because a cart is one more thing
 * to learn: at full speed a six-round room took them past seven o'clock and
 * the room's deadline, every time, and the write-up was for being new.
 */
export const LEARNER_PACE = 0.5;

/**
 * And on days four and five, three quarters: the first days with no hand on
 * the clock at all. Straight from half to full, a new starter's first full
 * day was a four-round room of forty chairs with a trade on it, set on foot
 * in four and a half hours against a deadline of four and three quarters,
 * missed, and written up; the second room that afternoon too, and the third
 * write-up was a suspension (`fullgame.mjs`, day four).
 */
export const SETTLING_PACE = 0.75;

/** Feet a lesson counts as "at" a thing from. */
const AT = 7;

/**
 * Day one, in order. Each lesson is the sentence (keys in keyboard words, made
 * the pad's at the screen; `pad` where the pad's way is a different sentence),
 * where it is (for the floor guide, the marker and the map), and the test that
 * it has been done. `count` fills a "{n} of {of}" in the sentence. Every
 * lesson before filing also counts as done once the room is filed, because the
 * event, the setup and what is in your hands are all gone again by then.
 */
export const LESSONS = Object.freeze([
  {
    id: 'to-locker',
    text: 'Welcome to your first day. Follow the gold arrows on the floor to your locker in the break room.',
    where: { prop: 'break-lockers' },
    done: (r) => r.filed || r.near('break-lockers', AT) || r.uniform,
  },
  {
    id: 'change',
    text: 'Press [E] at your locker to change into your uniform.',
    where: { prop: 'break-lockers' },
    done: (r) => r.filed || r.uniform,
  },
  {
    id: 'to-clock',
    text: 'Now follow the arrows to the time clock.',
    where: { prop: 'time-clock' },
    done: (r) => r.filed || r.near('time-clock', AT) || r.onTheClock,
  },
  {
    id: 'clock-in',
    text: 'Press [E] at the time clock to clock in for your shift.',
    where: { prop: 'time-clock' },
    done: (r) => r.filed || r.onTheClock,
  },
  {
    id: 'to-board',
    text: 'The BEO board beside the clock lists the rooms to set up. Walk up to it.',
    where: { prop: 'beo-board' },
    done: (r) => r.filed || r.near('beo-board', AT) || r.boardOpen || Boolean(r.active),
  },
  {
    id: 'open-board',
    text: 'Press [E] to read the board.',
    where: { prop: 'beo-board' },
    done: (r) => r.filed || r.boardOpen || Boolean(r.active),
  },
  {
    id: 'accept',
    text: 'This is your first room. Click Accept on its card.',
    pad: 'This is your first room. Move to Accept with the d-pad and press [E].',
    where: { prop: 'beo-board' },
    done: (r) => r.filed || Boolean(r.active),
  },
  {
    id: 'to-rounds',
    text: 'The room needs 2 round tables. They are stacked in Banquet Storage. Follow the arrows.',
    where: { prop: 'store-rounds' },
    done: (r) => r.filed || r.near('store-rounds', AT) || r.holding('round') || r.counts.tables >= r.want.tables,
  },
  {
    id: 'take-rounds',
    text: 'Press [E] at the stack to pick up the rounds.',
    where: { prop: 'store-rounds' },
    done: (r) => r.filed || r.holding('round') || r.counts.tables >= r.want.tables,
  },
  {
    id: 'set-rounds',
    text: 'Carry them to the Crown Ballroom. Walk onto a gold ring on the floor and press [E] to set a round there. ({n} of {of} set)',
    count: (r) => [r.counts.tables, r.want.tables],
    where: { anchor: 'round' },
    done: (r) => r.filed || r.counts.tables >= r.want.tables,
    // Out of rounds with the room not done: back to the stack.
    stuck: (r) => !r.holding('round') && r.counts.tables < r.want.tables,
    unstick: 'You need another round. Get one from the stack in Banquet Storage.',
    unstickWhere: { prop: 'store-rounds' },
  },
  {
    id: 'take-chairs',
    text: 'Each round needs {each} chairs. Get chairs from the stack in Banquet Storage: press [E] there.',
    where: { prop: 'store-chairs' },
    done: (r) => r.filed || r.holding('chair') || r.counts.chairs >= r.want.chairs,
  },
  {
    id: 'set-chairs',
    text: 'Walk up to a round and press [E] to set a chair at it. {each} at each round. ({n} of {of} set)',
    count: (r) => [r.counts.chairs, r.want.chairs],
    where: { anchor: 'seat' },
    done: (r) => r.filed || r.counts.chairs >= r.want.chairs,
    stuck: (r) => !r.holding('chair') && r.counts.chairs < r.want.chairs,
    unstick: 'You need more chairs. Get them from the stack in Banquet Storage.',
    unstickWhere: { prop: 'store-chairs' },
  },
  {
    id: 'take-linen',
    text: 'Last, each round needs a tablecloth. Press [E] at the linen shelf in Banquet Storage.',
    where: { prop: 'store-linen' },
    done: (r) => r.filed || r.holding('linen') || r.counts.linens >= r.want.tables,
  },
  {
    id: 'set-linen',
    text: 'Walk up to each round and press [E] to lay a cloth on it. ({n} of {of} done)',
    count: (r) => [r.counts.linens, r.want.tables],
    where: { anchor: 'cover' },
    done: (r) => r.filed || r.counts.linens >= r.want.tables,
    stuck: (r) => !r.holding('linen') && r.counts.linens < r.want.tables,
    unstick: 'You need another cloth. Get one from the linen shelf in Banquet Storage.',
    unstickWhere: { prop: 'store-linen' },
  },
  {
    id: 'file',
    text: 'The room is ready. Press [R] to file it.',
    where: { anchor: 'room' },
    done: (r) => r.filed,
  },
  {
    id: 'grade',
    text: 'This is your grade for the room. Press [Esc] to close it.',
    where: null,
    done: (r) => r.filed && !r.gradeOpen,
  },
  {
    id: 'to-clock-out',
    text: 'That is the work for today. Follow the arrows back to the time clock.',
    where: { prop: 'time-clock' },
    done: (r) => r.near('time-clock', AT) || r.shiftDone,
  },
  {
    id: 'clock-out',
    text: 'Press [E] at the time clock to clock out.',
    where: { prop: 'time-clock' },
    done: (r) => r.shiftDone,
  },
  {
    id: 'summary',
    text: 'This is the summary of your shift. Press [Esc] to close it.',
    where: null,
    done: (r) => r.shiftDone && !r.summaryOpen,
  },
  {
    id: 'to-cot',
    text: 'Follow the arrows to the cot in the break room.',
    where: { prop: 'break-cot' },
    done: (r) => r.near('break-cot', AT) || r.dayIndex > 1,
  },
  {
    id: 'sleep',
    text: 'Press [E] on the cot to sleep. Tomorrow you will learn a few new things.',
    where: { prop: 'break-cot' },
    done: (r) => r.dayIndex > 1,
  },
]);

/**
 * The first time each kept-back system is any use: one short instruction. Shown
 * one at a time, when `when` is true, until `done` is or `seconds` have gone.
 * `{wants}` and `{who}` are filled from the reading.
 */
export const TIPS = Object.freeze([
  {
    id: 'day-two',
    system: 'sheet',
    text: 'Day two. Clock in at the time clock with [E], then press [E] at the board and take a room, like yesterday. Any card will do: you cannot do them all, and the rest go ahead without you.',
    when: (r) => r.dayIndex >= 2 && !r.active,
    done: (r) => Boolean(r.active),
    where: (r) => (r.onTheClock ? { prop: 'beo-board' } : { prop: 'time-clock' }),
  },
  {
    id: 'sheet',
    system: 'sheet',
    text: 'New: hold [F] to read the sheet for your room. It shows where everything goes, and anything the room is short of.',
    when: (r) => Boolean(r.active),
    done: (r) => r.sheetSeen,
  },
  {
    id: 'marble',
    system: 'talk',
    text: 'Front of house is the guests\' side: staff in blacks out there get reported. Your room is sending you, so you are covered this time. Go straight there and straight back.',
    when: (r) => Boolean(r.sentFront),
    done: (r) => Boolean(r.inFront),
    seconds: 20,
  },
  {
    id: 'short',
    system: 'talk',
    text: 'Your room needs {wants}. Walk up to {who} and press [E] to talk, then {how}.',
    // Never over a job somebody is waiting on: asked again, they only say go
    // and do it, and a new starter went back and forth to them all afternoon.
    when: (r) => Boolean(r.short) && !r.errand,
    done: (r) => !r.short || Boolean(r.errand),
    where: (r) => (r.short?.at ? { point: r.short.at } : null),
  },
  {
    id: 'errand',
    system: 'wheel',
    text: 'They want a job done first: {errand}. Follow the arrows, and press [E] when you get there.',
    when: (r) => Boolean(r.errand),
    done: (r) => !r.errand,
    where: (r) => (r.errand?.at ? { point: r.errand.at } : null),
  },
  {
    id: 'wheel',
    system: 'wheel',
    text: 'Tip: hold [E] to see everything else you can do where you are standing. Let go on the one you want.',
    when: (r) => r.moreHere,
    done: (r) => r.fanned,
    seconds: 12,
  },
  {
    id: 'carts',
    system: 'carts',
    text: 'Tip: a table cart carries a whole room at once. Press [E] at one in Banquet Storage to push it.',
    when: (r) => r.room === 'storage' && Boolean(r.active),
    done: (r) => r.pushing,
    seconds: 12,
  },
  {
    id: 'snap',
    system: 'snap',
    text: 'Shortcut: press [C] to set what you are carrying on the nearest right spot.',
    when: (r) => r.canSnap,
    done: (r) => r.snapped,
    seconds: 12,
  },
  {
    id: 'classes',
    system: 'classes',
    text: 'New: a class is on today, {wants}. Follow the marker and press [E] there to join. Classes make you better at the work.',
    when: (r) => Boolean(r.classOn),
    done: (r) => r.satClass,
    where: (r) => (r.classOn?.at ? { point: r.classOn.at } : null),
    seconds: 25,
  },
  {
    id: 'throw',
    system: 'throw',
    text: 'New: hold [G] to wind up and let go to throw what you are holding. A short tap puts it down. In here, not in a corridor: things left in the way get reported.',
    // In the ballroom only. Taught in a corridor, it waited for a throw there,
    // and a chair put down in the corridor is a write-up (`fullgame.mjs`, day three).
    when: (r) => r.holdingSmall && r.room === 'ballroom',
    done: (r) => r.threw,
    seconds: 12,
  },
  {
    id: 'consumables',
    system: 'consumables',
    text: 'You have something in your pocket now. Hold [E] to use it. People may notice.',
    when: (r) => r.pocketFull,
    seconds: 12,
  },
  {
    id: 'strikes',
    system: 'strikes',
    text: 'New on the board: strikes. A strike is a lawn to clear: hitch its carts to the golf cart in the staff lot, tow them to the dock, and file it with [R] there.',
    when: (r) => r.strikeOnBoard,
    seconds: 14,
  },
  {
    id: 'jobs',
    system: 'jobs',
    text: 'New: daily jobs. Somebody with a gold ! over them has one for you: walk up and press [E] to take it. They are on the board too.',
    when: (r) => r.jobUp,
    seconds: 12,
  },
  {
    id: 'pranks',
    system: 'pranks',
    text: 'Some things here can be used for a prank. Hold [E] near them to see. If nobody sees you, it costs nothing.',
    when: (r) => r.prankHere,
    seconds: 12,
  },
]);

/** The sentence with its numbers in. */
function fill(text, values) {
  return text.replace(/\{(\w+)\}/g, (_, key) => (values[key] ?? '').toString());
}

export function createTutorial({ lessons = LESSONS, tips = TIPS, unlocks = UNLOCKS } = {}) {
  /** Null for a week with no tutorial. */
  let state = null;
  /** The tip on screen, and how long it has been up. Not saved: a tip can be shown again after a reload. */
  let tip = null;
  let tipFor = 0;

  const dayIndex = (day) => (state ? day - state.startDay + 1 : Infinity);

  return {
    /** A new starter's week: day one held by the hand. */
    begin(day) {
      state = { startDay: day, step: 0, seen: [] };
      tip = null;
    },
    clear() {
      state = null;
      tip = null;
    },
    get active() {
      return Boolean(state);
    },
    /** Whether day one's lessons are still running. */
    get guiding() {
      return Boolean(state) && state.step < lessons.length;
    },
    get step() {
      return state ? lessons[state.step]?.id ?? null : null;
    },
    /** How fast the clock should run: slowly until the first room is filed. */
    get pace() {
      if (!state) return 1;
      const filedAt = lessons.findIndex((lesson) => lesson.id === 'grade');
      return state.step < filedAt ? GUIDED_PACE : 1;
    },
    /** How fast the clock should run on a given day: the guided pace, half for two days, three quarters for two more. */
    paceOn(day) {
      if (!state) return 1;
      const filedAt = lessons.findIndex((lesson) => lesson.id === 'grade');
      if (state.step < filedAt) return GUIDED_PACE;
      const index = dayIndex(day);
      return index >= 2 && index <= 3 ? LEARNER_PACE : index >= 4 && index <= 5 ? SETTLING_PACE : 1;
    },
    /** Which day of the tutorial a day is, the hiring day being 1. */
    dayIndex,
    /** Whether a system is open yet on a given day. Everything is, with no tutorial. */
    allows(system, day) {
      if (!state) return true;
      const opens = unlocks[system];
      if (opens === undefined) return true;
      // Day one's lessons run past midnight only if you never sleep; the
      // systems wait for them all the same.
      if (state.step < lessons.length) return false;
      return dayIndex(day) >= opens;
    },

    /**
     * One frame. `reading` is the plain state of the game (see `main.js`,
     * `tutorialReading`). Back comes what to say, or null:
     * `{ kind: 'lesson'|'tip', id, text, pad, where, n, of }`.
     */
    frame(reading, dt = 1 / 60) {
      if (!state) return null;
      // Day one: one lesson at a time, each held until it is done. A lesson
      // already done when it comes up (you changed before reading it) is
      // passed over in the same frame.
      while (state.step < lessons.length && lessons[state.step].done(reading)) state.step += 1;
      const lesson = lessons[state.step];
      if (lesson) {
        const [n, of] = lesson.count ? lesson.count(reading) : [null, null];
        const values = { n, of, each: reading.want?.each ?? '' };
        if (lesson.stuck?.(reading)) {
          return { kind: 'lesson', id: lesson.id, text: fill(lesson.unstick, values), where: lesson.unstickWhere, step: state.step + 1, steps: lessons.length };
        }
        return {
          kind: 'lesson',
          id: lesson.id,
          text: fill(lesson.text, values),
          pad: lesson.pad ? fill(lesson.pad, values) : null,
          where: lesson.where,
          step: state.step + 1,
          steps: lessons.length,
        };
      }
      // After day one: the first time each system is any use, once.
      if (tip) {
        tipFor += dt;
        const row = tips.find((t) => t.id === tip);
        const over = !row || row.done?.(reading) || (row.seconds && tipFor >= row.seconds) || !this.allows(row.system, reading.day);
        if (over) {
          state.seen.push(tip);
          tip = null;
        }
      }
      if (!tip) {
        const next = tips.find((t) => !state.seen.includes(t.id) && this.allows(t.system, reading.day) && t.when(reading));
        if (next) {
          tip = next.id;
          tipFor = 0;
        }
      }
      if (!tip) return null;
      const row = tips.find((t) => t.id === tip);
      const values = {
        wants: reading.short?.wants ?? reading.classOn?.name ?? '',
        who: reading.short?.who ?? '',
        how: reading.short?.how ?? 'press [E] again to ask',
        errand: reading.errand?.label ?? '',
      };
      return { kind: 'tip', id: row.id, text: fill(row.text, values), where: row.where?.(reading) ?? null };
    },

    toJSON: () => (state ? { ...state, seen: [...state.seen] } : null),
    load(saved) {
      state = saved ? { startDay: saved.startDay ?? 1, step: saved.step ?? 0, seen: [...(saved.seen ?? [])] } : null;
      tip = null;
    },
  };
}
